import { useEffect, useState } from "react";
import { CalendarClock, ExternalLink, FileArchive, FileCode2, FileImage, FileSpreadsheet, FileText, HardDriveDownload, Paperclip } from "lucide-react";

import Fetching from "@/components/Fetching/Fetching";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import { useOADocuments } from "@/hooks/planning/oa/useOA";

import styles from "./files-modal.module.scss";

const FilesModal = ({ relatedTable, externalCode, onClose }) => {
	const [documents, setDocuments] = useState([]);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
	const [documentsLoadFailed, setDocumentsLoadFailed] = useState(false);
	const [documentsLoadErrorMessage, setDocumentsLoadErrorMessage] = useState("");

	const { mutateAsync: getDocuments } = useOADocuments();

	const getExtensionLabel = (extensionValue) =>
		String(extensionValue || "")
			.toLowerCase()
			.trim();

	const getFileIcon = (extensionValue) => {
		const extensionLabel = getExtensionLabel(extensionValue);

		if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(extensionLabel)) {
			return FileImage;
		}

		if (["xls", "xlsx", "csv"].includes(extensionLabel)) {
			return FileSpreadsheet;
		}

		if (["zip", "rar", "7z", "tar", "gz"].includes(extensionLabel)) {
			return FileArchive;
		}

		if (["json", "xml", "js", "ts", "sql", "yaml", "yml"].includes(extensionLabel)) {
			return FileCode2;
		}

		if (["pdf", "txt", "doc", "docx", "ppt", "pptx"].includes(extensionLabel)) {
			return FileText;
		}

		return Paperclip;
	};

	const formatBytes = (bytesValue) => {
		const bytesNumber = Number(bytesValue || 0);
		if (!bytesNumber || bytesNumber <= 0) {
			return "0 B";
		}

		const sizeUnits = ["B", "KB", "MB", "GB", "TB"];
		const sizeIndex = Math.min(Math.floor(Math.log(bytesNumber) / Math.log(1024)), sizeUnits.length - 1);
		const normalizedSize = bytesNumber / 1024 ** sizeIndex;
		const sizeText = normalizedSize >= 10 ? normalizedSize.toFixed(0) : normalizedSize.toFixed(1);

		return `${sizeText} ${sizeUnits[sizeIndex]}`;
	};

	const formatCreatedDate = (dateValue) => {
		if (!dateValue) {
			return "Sin fecha";
		}

		const parsedDate = new Date(dateValue);
		if (Number.isNaN(parsedDate.getTime())) {
			return "Sin fecha";
		}

		return parsedDate.toLocaleString("es-CL", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	useEffect(() => {
		const loadDocuments = async () => {
			setIsLoadingDocuments(true);
			setDocumentsLoadFailed(false);
			setDocumentsLoadErrorMessage("");

			try {
				const files = await getDocuments({
					external_id: Number(externalCode),
					related_table: relatedTable,
				});
				setDocuments(Array.isArray(files) ? files : []);
			} catch (error) {
				setDocumentsLoadFailed(true);
				setDocumentsLoadErrorMessage(error?.message || "Error al consultar archivos.");
				setDocuments([]);
			} finally {
				setIsLoadingDocuments(false);
			}
		};

		loadDocuments();
	}, [externalCode, getDocuments, relatedTable]);

	const renderDocumentsContent = () => {
		if (isLoadingDocuments) {
			return <Fetching topMargin="0px" bottomMargin="0px" />;
		}

		if (documents.length > 0) {
			return (
				<div className={styles.documentsList}>
					{documents.map((file, index) => (
						<div key={`${file.url || file.name || "document"}-${index}`} className={styles.documentItem}>
							<div className={styles.fileIdentity}>
								<div className={styles.fileIconWrap}>
									{(() => {
										const FileIcon = getFileIcon(file.extension);
										return <FileIcon size={18} />;
									})()}
								</div>
								<div className={styles.fileTexts}>
									<div className={styles.documentName}>{file.name || `Archivo ${index + 1}`}</div>
									<div className={styles.fileMetaRow}>
										<span className={styles.fileMetaChip}>{getExtensionLabel(file.extension) || "archivo"}</span>
										<span className={styles.fileMetaDot}>•</span>
										<span className={styles.fileMetaText}>
											<HardDriveDownload size={12} />
											{formatBytes(file.size)}
										</span>
										<span className={styles.fileMetaDot}>•</span>
										<span className={styles.fileMetaText}>
											<CalendarClock size={12} />
											{formatCreatedDate(file.created_at)}
										</span>
									</div>
								</div>
							</div>
							<button
								type="button"
								className={styles.openFileButton}
								onClick={() => {
									window.open(file.url, "_blank", "noopener,noreferrer");
								}}
							>
								Abrir
								<ExternalLink size={14} />
							</button>
						</div>
					))}
				</div>
			);
		}

		if (documentsLoadFailed) {
			return <div className={styles.emptyDocuments}>{documentsLoadErrorMessage || "No fue posible cargar los archivos."}</div>;
		}

		return <div className={styles.emptyDocuments}>No se encontraron archivos para este registro.</div>;
	};

	return (
		<CustomModalForm open onClose={onClose} title="Archivos Adjuntos" size="md" showSave={false} gridTable={false}>
			<div className={styles.documentsContainer}>
				<div className={styles.modalSubtitle}>Listado de documentos vinculados al registro seleccionado.</div>
				{renderDocumentsContent()}
			</div>
		</CustomModalForm>
	);
};

export default FilesModal;
